import { useMemo } from "react";
import { defineMessage, useIntl } from "react-intl";
import {
  Environment,
  graphql,
  loadQuery,
  usePreloadedQuery,
  useSubscription,
} from "react-relay";
import { GraphQLSubscriptionConfig } from "relay-runtime";
import ErrorPage from "@/pages/_error";
import CompetitionApprovals from "@/components/CompetitionApprovals";
import CompetitionJury from "@/components/CompetitionJury";
import { CompetitionApprovalPageSubscription as CompetitionApprovalPageSubscriptionType } from "./__generated__/CompetitionApprovalPageSubscription.graphql";
import { LoaderArgs } from "@/utils/route";
import { createIntl } from "@/utils/intl";
import { getSearchParams } from "@/utils/validation";
import { CompetitionApprovalPageQuery as CompetitionApprovalPageQueryType } from "./__generated__/CompetitionApprovalPageQuery.graphql";
import { schemaFactory } from "@/components/CompetitionApprovals/schema";
import { safeParse } from "valibot";
import { useLoaderData } from "react-router";
const CompetitionApprovalPageSubscription = graphql`
  subscription CompetitionApprovalPageSubscription($competitionId: ID!) {
    projectVersionStatusUpdate(competitionId: $competitionId) {
      project {
        latest {
          ...SubmissionStatusBadgeFragment
          ...BaseProjectVersionApprovalButtonFragment
        }
      }
    }
  }
`;
export default function CompetitionApprovalPage() {
  const intl = useIntl();
  const {
    query: { competitionBySlug: competition },
    searchParams,
  } = useCompetitionApprovalPageQuery();
  useSubscription(
    useMemo(
      (): GraphQLSubscriptionConfig<CompetitionApprovalPageSubscriptionType> => ({
        variables: { competitionId: competition?.id || "" },
        subscription: CompetitionApprovalPageSubscription,
        onError: (error) => {
          if (competition?.id) {
            console.error(error);
          }
        },
      }),
      [competition?.id],
    ),
  );
  if (!competition) {
    return (
      <ErrorPage
        status={404}
        message={intl.formatMessage({
          defaultMessage: "Competition not found",
        })}
      />
    );
  }
  return (
    <div className="pb-8 flex flex-col gap-8">
      <CompetitionJury competition={competition} />
      <CompetitionApprovals
        competition={competition}
        defaultFilters={searchParams}
      />
    </div>
  );
}
const CompetitionApprovalPageQuery = graphql`
  query CompetitionApprovalPageQuery(
    $owner: String!
    $slug: String!
    $approvalFilter: ApprovalFilter
    $stage: Int
  ) {
    competitionBySlug(slug: $slug) {
      id
      title
      ...CompetitionJuryFragment
      ...CompetitionApprovalsFragment
        @arguments(
          owner: $owner
          approvalFilter: $approvalFilter
          stage: $stage
        )
    }
  }
`;
export const loader = (environment: Environment) => {
  return ({ params: { slug: owner, track: slug }, request }: LoaderArgs) => {
    if (!slug || !owner) {
      throw new Response("No slug or owner", { status: 500 });
    }
    const schema = schemaFactory(createIntl());
    const searchParams = getSearchParams(request);
    const parseResult = safeParse(schema, searchParams);
    const variables = {
      slug,
      owner,
      ...(parseResult.success ? parseResult.output : undefined),
    };
    return {
      graphql: CompetitionApprovalPageQuery,
      variables,
      query: loadQuery<CompetitionApprovalPageQueryType>(
        environment,
        CompetitionApprovalPageQuery,
        variables,
      ),
      searchParams,
    };
  };
};
type LoaderData = ReturnType<ReturnType<typeof loader>>;
const useCompetitionApprovalPageQuery = () => {
  const {
    graphql,
    query: preloadedQuery,
    ...data
  } = useLoaderData() as LoaderData;
  const query = usePreloadedQuery<CompetitionApprovalPageQueryType>(
    graphql,
    preloadedQuery,
  );
  return {
    query,
    ...data,
  };
};

export const lazyHandle = {
  titleI18n: defineMessage({ defaultMessage: "Approval" }),
};
